<script>
/*
  The km-tooltip component is based on the v-tooltip (vuetify tooltip) component but its goal is to
  be more efficient performance-wise and a bit easier to use. To achieve this, a single v-tooltip is
  rendered in the page (the KmTooltip/Portal component), driven by the km-tooltip component in
  various components. The km-tooltip component accepts two slots:
  * 'default' (either normal or scoped) to specify what should be displayed *inside* the tooltip
  * 'activator' (normal) to specify which element/component should trigger the tooltip when hovered.

  Contrary to v-tooltip, km-tooltip won't wrap the 'activator' component into a DOM element. It will
  only attach some listeners to show/hide the global tooltip.

  The 'activator' is optional and can be replaced by the 'target' prop.  The 'target' prop, if
  specified, should be a DOM element used to compute the tooltip position, or 'null' to hide the
  tooltip.
*/
import { emit } from './controller'
import runDelayedMixin from '@/mixins/runDelayedMixin'
export { default as Portal } from './Portal'

export default {
  name: 'KmTooltip',

  mixins: [runDelayedMixin],

  props: {
    // Disable the tooltip: it will become hidden and won't show up on mouse over
    disabled: Boolean,

    // If no 'activator' slot is defined, this prop will be used to control the position of the
    // tooltip.
    target: Element,

    // Supported v-tooltip props:
    //    * bottom / top / left / right
    //    * max-width / min-width
    //    * nudge-left / nudge-right / nudge-top / nudge-width
  },

  computed: {
    hasActivator() {
      return Boolean(this.$slots.activator)
    },
  },

  mounted() {
    this._hovered = false
    this._shown = false

    if (this.hasActivator) {
      this.$el.addEventListener('mouseenter', this.onMouseEnter)
      this.$el.addEventListener('mouseleave', this.onMouseLeave)
    }
  },

  beforeDestroy() {
    if (this._shown) {
      this.emitLeave()
    }
    if (this.hasActivator) {
      this.$el.removeEventListener('mouseenter', this.onMouseEnter)
      this.$el.removeEventListener('mouseleave', this.onMouseLeave)
    }
  },

  updated() {
    if (this._shown) {
      if (this.disabled || (!this.hasActivator && !this.target)) {
        // The tooltip becomes disabled or does not have a target anymore, hide it
        this.emitLeave()
      } else {
        // Else update the content
        this.emitEnter()
      }
    } else if (!this.disabled && (this._hovered || (!this.hasActivator && this.target))) {
      // The element is hovered and becomes enabled, show it
      this.emitEnter()
    }
  },

  methods: {
    emitEnter() {
      // The tooltip is already scheduled to be shown, nothing to do
      if (this.hasDelayed('show')) return

      // Delay the tooltip 'show' event.  This fixes the tooltip to keep being shown when hiding and
      // showing it too quickly.  See https://github.com/vuetifyjs/vuetify/issues/5740
      const delay = this._shown ? 0 : 400

      this._shown = true

      this.runDelayed(
        'show',
        () => {
          let content
          if (this.$slots.default) {
            content = this.$slots.default
          } else if (this.$scopedSlots.default) {
            content = this.$scopedSlots.default({})
          }

          emit(this, {
            activator: this.hasActivator ? this.$el : this.target,
            content,
            attrs: this.$attrs,
          })
        },
        delay,
      )
    },

    emitLeave() {
      this._shown = false
      this.cancelDelayed('show')
      emit(this, false)
    },

    onMouseEnter(event) {
      this._hovered = true
      if (!this.disabled) {
        this.emitEnter()
      }
    },

    onMouseLeave() {
      this._hovered = false
      if (!this.disabled) {
        this.emitLeave()
      }
    },
  },

  render(h) {
    if (!this.hasActivator) return
    if (this.$slots.activator.length !== 1) {
      throw new Error('KmTooltip: the activator should be exactly one node')
    }
    return this.$slots.activator[0]
  },
}
</script>
